import { useCallback, useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../../utils/api";
import { getError } from "../../utils/getError";
import { qk } from "../../lib/queryKeys";
import { trackingLinkQueryOptions, useTrackingLink } from "../trackingLinks/queries";
import { useCompaniesStore } from "../../store/admin/companiesStore";
import { useShipmentUnit } from "../../Layouts/envios/hooks/useShipmentUnit";
import { useUpdateShipment } from "./mutations";
import type { TrackingLink } from "../../store/admin/trackingLinksStore";
import type { Shipment } from "./types";

const DEFAULT_DURATION_VALUE = 1;
const DEFAULT_DURATION_UNIT = "days";

const LINK_STATE_BY_STATUS_ID = {
  R5tW0uX4yY9cF6dH2aI8: "active",
  W0yB5zC9dD4hK1iM7fN3: "expired",
  S6uX1vY5zZ0dG7eI3bJ9: "inactive",
} as const;

export type ShipmentLinkState =
  | "missing"
  | (typeof LINK_STATE_BY_STATUS_ID)[keyof typeof LINK_STATE_BY_STATUS_ID];

const STATE_BY_STATUS_ID: Record<string, ShipmentLinkState> =
  LINK_STATE_BY_STATUS_ID;

const resolveLinkState = (link: TrackingLink | null): ShipmentLinkState =>
  link ? (STATE_BY_STATUS_ID[link.status_id] ?? "missing") : "missing";

export const trackingLinkUrl = (publicUrl: string): string =>
  `${window.location.origin}${publicUrl}`;

const unwrapTrackingLink = (payload: unknown): TrackingLink | null => {
  const body = payload as { data?: { data?: TrackingLink } & TrackingLink };
  return body?.data?.data ?? body?.data ?? null;
};

export function useShipmentTrackingLink(
  shipment: Shipment | undefined,
  options: { watch?: boolean } = {},
) {
  const watch = options.watch ?? true;
  const companyId = useCompaniesStore((s) => s.companyId);
  const queryClient = useQueryClient();
  const updateShipment = useUpdateShipment();

  const linkId = shipment?.tracking_link_id ?? null;
  const unit = useShipmentUnit(shipment?.actual_device_id ?? null);
  const vehicleId = unit?.vehicleId || null;
  const { data, isLoading } = useTrackingLink(linkId, watch);
  const link = (data ?? null) as TrackingLink | null;

  const state = useMemo(() => resolveLinkState(link), [link]);

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["trackingLinks"] });
    queryClient.invalidateQueries({ queryKey: qk.counts(companyId) });
  }, [queryClient, companyId]);

  const createLink = useMutation({
    mutationFn: async (): Promise<TrackingLink> => {
      const res = await api.post("/trackingLinks", {
        name: shipment?.tracking_number || shipment?.client_name || "",
        internalNotes: shipment?.client_name ?? "",
        units: vehicleId ? [vehicleId] : [],
        shareTrips: true,
        accessType: "public",
        activationType: "immediate",
        durationValue: DEFAULT_DURATION_VALUE,
        durationUnit: DEFAULT_DURATION_UNIT,
        company_id: companyId,
      });
      const created = unwrapTrackingLink(res.data);
      if (!created?.publicUrl || !created?.id) throw new Error("missingPublicUrl");
      if (shipment) {
        await updateShipment.mutateAsync({
          id: shipment.id,
          data: { tracking_link_id: created.id },
        });
      }
      return created;
    },
    onSuccess: invalidate,
  });

  const renewLink = useMutation({
    mutationFn: async (): Promise<void> => {
      if (!linkId) throw new Error("noLink");
      await api.put(
        "/trackingLinks/extend",
        {
          durationValue: DEFAULT_DURATION_VALUE,
          durationUnit: DEFAULT_DURATION_UNIT,
        },
        { params: { id: linkId } },
      );
    },
    onSuccess: invalidate,
  });

  const resolveCurrent = useCallback(async (): Promise<TrackingLink | null> => {
    if (!linkId) return null;
    if (watch) return link;
    return queryClient.fetchQuery(trackingLinkQueryOptions(companyId, linkId));
  }, [linkId, watch, link, queryClient, companyId]);

  const ensure = useCallback(async (): Promise<string> => {
    const current = await resolveCurrent();
    const currentState = resolveLinkState(current);
    if (currentState === "active" && current?.publicUrl) {
      return trackingLinkUrl(current.publicUrl);
    }
    if (currentState !== "missing") throw new Error("linkNotActive");
    if (!vehicleId) throw new Error("noUnit");
    if (!companyId) throw new Error("noCompany");
    try {
      const created = await createLink.mutateAsync();
      return trackingLinkUrl(created.publicUrl);
    } catch (error) {
      throw new Error(getError(error));
    }
  }, [resolveCurrent, vehicleId, companyId, createLink]);

  const renew = useCallback(async (): Promise<void> => {
    try {
      await renewLink.mutateAsync();
    } catch (error) {
      throw new Error(getError(error));
    }
  }, [renewLink]);

  return {
    state,
    url: link?.publicUrl ? trackingLinkUrl(link.publicUrl) : null,
    hasLink: !!linkId,
    hasUnit: !!vehicleId,
    hasCompany: !!companyId,
    isLoading,
    isCreating: createLink.isPending,
    isRenewing: renewLink.isPending,
    ensure,
    renew,
  };
}
